import usePageTitle from "../../utils/usePageTitle";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  analyze_recording,
  get_all_recordings,
  update_recording_name,
  set_recording_inclusion,
  delete_recording,
} from "../../api";
import { useLogin } from "../../reducers";
import { parseUtcDate } from "../../utils";
import { useSettings } from "../../utils/SettingsContext";
import useRecordingControls from "../../utils/useRecordingControls";
import type { SessionAction } from "./MySessionRow";
import MyActivity from "./MyActivity";
import { useRecordingList } from "./useRecordingList";

export default function PersonalSessions({ peopleControl }: { peopleControl?: React.ReactNode }) {
  const { t } = useTranslation();
  const { user } = useLogin();
  const { autoAnalyze } = useSettings();
  const {
    recordingSyncStatus,
    recordingSyncCompletionId,
    handleRetryRecordingSync,
    pendingReviewCount,
    applyRecordingStatus,
  } = useRecordingControls();
  usePageTitle(t("activity.my_title"));
  const fetchOwn = useCallback(async () => {
    const result = await get_all_recordings({ user_ids: [user.id] });
    return result.recordings
      .filter(item => item.user_id === user.id)
      .sort(
        (a, b) => parseUtcDate(b.date_started).getTime() - parseUtcDate(a.date_started).getTime(),
      );
  }, [user.id]);
  const {
    recordings: allRecordings,
    update,
    loading,
    error,
    updateError,
    refresh,
  } = useRecordingList({
    fetch: fetchOwn,
    key: user.id,
    ownerId: user.id,
    autoAnalyze,
    recordingSyncCompletionId,
  });

  return (
    <MyActivity
      onRename={async (id, name) => {
        const updated = await update_recording_name(id, name);
        update(items =>
          items.map(item => (item.id === id ? { ...item, name: updated.name } : item)),
        );
      }}
      onAction={async (action: SessionAction, id: string) => {
        if (action === "delete") {
          await delete_recording(id);
          update(items => items.filter(item => item.id !== id));
        } else {
          const updated = await set_recording_inclusion(id, action === "unarchive");
          update(items =>
            items.map(item =>
              item.id === id
                ? { ...item, include_in_processes: updated.include_in_processes }
                : item,
            ),
          );
        }
      }}
      onAnalyze={async id => {
        const status = await analyze_recording(id);
        update(items => items.map(item => (item.id === id ? { ...item, status } : item)));
      }}
      peopleControl={peopleControl}
      recordings={allRecordings}
      userId={user.id}
      loading={loading}
      error={error}
      updateError={updateError}
      refresh={refresh}
      syncStatus={recordingSyncStatus}
      retrySync={handleRetryRecordingSync}
      pendingReviewCount={pendingReviewCount}
      onReviewStatus={applyRecordingStatus}
    />
  );
}
